import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BACKEND_MAIN_URL } from '../config';
import { Capacitor } from '@capacitor/core';
import authHeaders from '../authHeaders';
import { Geolocation } from '@capacitor/geolocation';

const STORAGE_KEY = 'upya_weather_location';

// ─── WMO weather-code → icon + human label ────────────────────────────
// https://open-meteo.com/en/docs — weather_code interpretation.
// We keep it compact: one entry per family (clear / cloudy / rain / snow /
// storm / fog), with a day/night flag driving the icon.
const wmoToView = (code, isDay = true) => {
  const day = isDay;
  if (code === 0) return { icon: day ? '☀️' : '🌙', label: day ? 'Clear sky' : 'Clear night', accent: '#f59e0b' };
  if (code === 1) return { icon: day ? '🌤️' : '🌙', label: 'Mostly clear', accent: '#f59e0b' };
  if (code === 2) return { icon: day ? '⛅' : '☁️', label: 'Partly cloudy', accent: '#0284c7' };
  if (code === 3) return { icon: '☁️', label: 'Overcast', accent: '#64748b' };
  if (code === 45 || code === 48) return { icon: '🌫️', label: 'Foggy', accent: '#64748b' };
  if ([51, 53, 55, 56, 57].includes(code)) return { icon: '🌦️', label: 'Drizzle', accent: '#0284c7' };
  if ([61, 63, 65, 66, 67].includes(code)) return { icon: '🌧️', label: 'Rain', accent: '#1d4ed8' };
  if ([71, 73, 75, 77].includes(code)) return { icon: '🌨️', label: 'Snow', accent: '#0ea5e9' };
  if ([80, 81, 82].includes(code)) return { icon: '🌧️', label: 'Rain showers', accent: '#1d4ed8' };
  if ([85, 86].includes(code)) return { icon: '🌨️', label: 'Snow showers', accent: '#0ea5e9' };
  if ([95, 96, 99].includes(code)) return { icon: '⛈️', label: 'Thunderstorm', accent: '#7c3aed' };
  return { icon: day ? '🌤️' : '🌙', label: 'Weather', accent: '#0284c7' };
};

// Gradient background driven by weather family — matches a modern weather-app
// feel (sunny = warm gradient, rainy = cool blue, cloudy = neutral).
const gradientFor = (code, isDay = true) => {
  if (code === 0 || code === 1) {
    return isDay
      ? 'linear-gradient(160deg, #fef3c7 0%, #ffffff 55%, #f0fdf4 100%)'
      : 'linear-gradient(160deg, #dbeafe 0%, #f8fafc 62%, #ffffff 100%)';
  }
  if (code === 2 || code === 3) return 'linear-gradient(160deg, #e0f2fe 0%, #ffffff 55%, #f8fafc 100%)';
  if (code === 45 || code === 48) return 'linear-gradient(160deg, #f1f5f9 0%, #ffffff 100%)';
  if ([51, 53, 55, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return 'linear-gradient(160deg, #dbeafe 0%, #ffffff 55%, #eff6ff 100%)';
  if ([95, 96, 99].includes(code)) return 'linear-gradient(160deg, #ede9fe 0%, #ffffff 55%, #f5f3ff 100%)';
  return 'linear-gradient(160deg, #f0fdf4 0%, #ffffff 100%)';
};

// Advice items are no longer color-coded — keeping the visual quiet lets the
// richer LLM-written labels breathe. Alerts still carry the urgency colour.

const ALERT_STYLES = {
  info: { bg: 'linear-gradient(90deg, #dbeafe, #eff6ff)', border: '#38bdf8', color: '#075985', icon: 'ℹ️' },
  warn: { bg: 'linear-gradient(90deg, #fef3c7, #fffbeb)', border: '#f59e0b', color: '#78350f', icon: '⚠️' },
};

const readSavedLocation = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const saveLocation = (loc) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch {
    /* ignore quota / private mode */
  }
};

// Thrown when the upstream lookup itself failed, as opposed to the city simply
// not existing — the two need different messages, and conflating them is how
// #83 stayed hidden: a CSP-blocked request read as "City not found".
class GeocodeUnavailable extends Error {}

const geocodeCity = async (query, lang) => {
  // Goes through our backend, not Open-Meteo directly: the page's CSP does not
  // allow the geocoding host in connect-src, so a direct browser call is
  // blocked before it is ever sent (#83).
  const url = new URL(`${BACKEND_MAIN_URL}/api/weather/geocode`);
  url.searchParams.set('q', query);
  url.searchParams.set('lang', lang || 'en');
  const resp = await fetch(url.toString(), { headers: authHeaders() });
  if (!resp.ok) throw new GeocodeUnavailable(`HTTP ${resp.status}`);
  const data = await resp.json();
  const hit = (data.results || [])[0];
  if (!hit) throw new Error('city not found');
  return { lat: hit.lat, lon: hit.lon, label: hit.label };
};

const fmtTime = (iso, lang) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleTimeString(lang || undefined, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
};

const compass = (deg) => {
  if (deg == null) return '';
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round(deg / 45) % 8];
};

const fmtDayShort = (iso, lang) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(lang || undefined, { weekday: 'short' });
  } catch {
    return iso;
  }
};

const isMobileViewport = () =>
  typeof window !== 'undefined' && window.innerWidth <= 900;

const WeatherCard = ({ compact = false }) => {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || 'en').split('-')[0];

  // Compact entry keeps weather secondary to the conversation on every screen.
  // The user's explicit expand/collapse preference remains respected.
  const [expanded, setExpandedState] = useState(() => {
    try {
      const saved = localStorage.getItem('upya_weather_expanded');
      if (saved === 'true') return true;
      if (saved === 'false') return false;
    } catch { /* ignore */ }
    return !compact && !isMobileViewport();
  });
  const setExpanded = (value) => {
    setExpandedState(value);
    try { localStorage.setItem('upya_weather_expanded', String(value)); } catch { /* ignore */ }
  };

  const [location, setLocation] = useState(readSavedLocation);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [manualMode, setManualMode] = useState(false);
  const [permissionState, setPermissionState] = useState('idle');

  useEffect(() => {
    if (location?.lat == null || location?.lon == null) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    const url = new URL(`${BACKEND_MAIN_URL}/api/weather`);
    url.searchParams.set('lat', String(location.lat));
    url.searchParams.set('lon', String(location.lon));
    url.searchParams.set('lang', lang);
    fetch(url.toString(), { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((payload) => { if (!cancelled) setData(payload); })
      .catch((err) => { if (!cancelled) setError(err.message || String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [location, lang]);

  const requestGeolocation = async () => {
    setPermissionState('requesting');
    setError(null);
    try {
      let pos;
      if (Capacitor.isNativePlatform()) {
        const permission = await Geolocation.requestPermissions({ permissions: ['location'] });
        if (permission.location !== 'granted' && permission.coarseLocation !== 'granted') throw new Error('Location permission denied');
        pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: false, timeout: 20000, maximumAge: 60 * 60 * 1000 });
      } else {
        if (!navigator.geolocation) throw new Error('Geolocation unavailable');
        pos = await new Promise((resolve, reject) => navigator.geolocation.getCurrentPosition(resolve, reject,
          { timeout: 20000, maximumAge: 60 * 60 * 1000 }));
      }
      const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude, label: null };
      saveLocation(loc);
      setLocation(loc);
      setPermissionState('idle');
    } catch {
      setPermissionState('denied');
      setManualMode(true);
      setError(t('workspace:weatherLocationDenied'));
    }
  };

  const submitCity = async (e) => {
    e.preventDefault();
    if (!cityInput.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const loc = await geocodeCity(cityInput.trim(), lang);
      saveLocation(loc);
      setLocation(loc);
      setManualMode(false);
      setCityInput('');
    } catch (err) {
      setError(err instanceof GeocodeUnavailable
        ? t('weather.error_search_unavailable', 'City search is unavailable right now. Please try again.')
        : t('weather.error_city_not_found', 'City not found'));
    } finally {
      setLoading(false);
    }
  };

  const resetLocation = () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setLocation(null);
    setData(null);
    setError(null);
    setManualMode(false);
    setPermissionState('idle');
  };

  const w = data?.weather || {};
  const advice = data?.advice || [];
  const alerts = data?.alerts || [];
  const view = wmoToView(w.weather_code, w.is_day !== false);
  const gradient = gradientFor(w.weather_code, w.is_day !== false);
  const conditionLabel = (code, isDay = true) => {
    const key = code === 0 ? (isDay ? 'weatherClear' : 'weatherNight')
      : code === 1 || code === 2 ? 'weatherPartlyCloudy'
      : code === 3 ? 'weatherCloudy'
      : code === 45 || code === 48 ? 'weatherFog'
      : [51, 53, 55, 56, 57].includes(code) ? 'weatherDrizzle'
      : [71, 73, 75, 77, 85, 86].includes(code) ? 'weatherSnow'
      : [95, 96, 99].includes(code) ? 'weatherStorm'
      : [61, 63, 65, 66, 67, 80, 81, 82].includes(code) ? 'weatherRain' : 'weatherLocal';
    return t(`workspace:${key}`);
  };

  // A town the user searched for keeps its own name: the bulletin's location
  // falls back to bare coordinates whenever the backend's reverse lookup fails.
  const displayLocation = useMemo(() => {
    if (location?.label) return location.label;
    if (w.location) return w.location;
    if (location?.lat != null && location?.lon != null) return `${location.lat.toFixed(2)}°, ${location.lon.toFixed(2)}°`;
    return '';
  }, [w.location, location]);

  // ── Shared card shell ──
  const shell = (children, bg) => (
    <div
      className="weather-card"
      style={{
        background: bg || 'linear-gradient(160deg, #f0fdf4 0%, #ffffff 55%, #ecfeff 100%)',
        border: '1px solid rgba(148, 163, 184, 0.2)',
        borderRadius: 20,
        padding: 20,
        marginTop: 4,
        boxShadow: '0 10px 30px -12px rgba(15, 23, 42, 0.15), 0 2px 4px rgba(15, 23, 42, 0.04)',
        color: '#0f172a',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <button
        type="button"
        onClick={() => setExpanded(false)}
        style={{
          display: 'block', width: '100%', marginBottom: 12,
          padding: '5px 0', borderRadius: 8,
          border: '1px solid rgba(148, 163, 184, 0.25)',
          background: 'rgba(255,255,255,0.6)', color: '#64748b',
          fontSize: '0.75rem', fontWeight: 600, cursor: 'pointer',
        }}
      >
        ▴ {t('weather.collapse', 'Hide')}
      </button>
      {children}
    </div>
  );

  // ── Collapsed compact bar (mobile) ──
  if (!expanded) {
    const hasData = data?.weather && data.weather.temperature_c != null;
    const collapsedView = wmoToView(data?.weather?.weather_code, data?.weather?.is_day !== false);
    return (
      <button
        type="button"
        className="weather-card"
        onClick={() => setExpanded(true)}
        style={{
          display: 'flex', alignItems: 'center', gap: 10, width: '100%',
          padding: '10px 14px', marginTop: 4,
          background: 'linear-gradient(135deg, #f0fdf4 0%, #ffffff 60%, #ecfeff 100%)',
          border: '1px solid rgba(148, 163, 184, 0.25)', borderRadius: 14,
          boxShadow: '0 4px 14px -6px rgba(15, 23, 42, 0.15)',
          cursor: 'pointer', textAlign: 'left', color: '#0f172a',
        }}
      >
        <span style={{ fontSize: '1.4rem' }}>{hasData ? collapsedView.icon : '🌦️'}</span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontWeight: 700, fontSize: '0.85rem' }}>
            {hasData
              ? `${Math.round(data.weather.temperature_c)}°C · ${conditionLabel(data.weather.weather_code, data.weather.is_day !== false)}`
              : t('weather.title', "Today's weather")}
          </span>
          <span style={{ display: 'block', color: '#64748b', fontSize: '0.72rem' }}>
            {hasData
              ? `💧 ${data.weather.rain_probability_pct != null ? `${Math.round(data.weather.rain_probability_pct)}%` : '—'} · ${t('weather.tap_details', 'Tap for advice & forecast')}`
              : t('weather.tap_setup', 'Tap to set up')}
          </span>
        </span>
        <span style={{ color: '#94a3b8', fontSize: '0.9rem' }}>▾</span>
      </button>
    );
  }

  // ─────────── Onboarding ───────────
  if (!location) {
    return shell(
      <>
        <div style={{ fontSize: 44, textAlign: 'center', marginBottom: 6 }}>🌦️</div>
        <div style={{ fontWeight: 800, fontSize: '1.05rem', textAlign: 'center', marginBottom: 6 }}>
          {t('weather.title', "Today's weather")}
        </div>
        <p style={{ margin: '0 0 16px', color: '#475569', textAlign: 'center', fontSize: '0.88rem', lineHeight: 1.4 }}>
          {t('weather.intro', 'Share your location for weather-based advice tailored to your farm.')}
        </p>

        {!manualMode && (
          <button
            type="button"
            onClick={requestGeolocation}
            disabled={permissionState === 'requesting'}
            style={{
              width: '100%', padding: '10px 14px', borderRadius: 12, border: 'none',
              background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
              color: 'white', fontWeight: 700, cursor: 'pointer', fontSize: '0.95rem',
              boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
              transition: 'transform 0.15s ease, box-shadow 0.15s ease',
              marginBottom: 10,
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
            }}
            onMouseOver={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
            onMouseOut={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
          >
            <span>📍</span>
            <span>{permissionState === 'requesting' ? t('weather.locating', 'Locating…') : t('weather.allow_location', 'Use my location')}</span>
          </button>
        )}

        {!manualMode && (
          <button
            type="button"
            onClick={() => setManualMode(true)}
            style={{
              width: '100%', padding: '8px 14px', borderRadius: 10,
              border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer',
              color: '#334155', fontSize: '0.85rem', fontWeight: 500,
            }}
          >
            🔍 {t('weather.enter_city', 'Enter a city instead')}
          </button>
        )}

        {manualMode && (
          <form onSubmit={submitCity} style={{ display: 'flex', gap: 6 }}>
            <input
              type="text"
              value={cityInput}
              onChange={(e) => setCityInput(e.target.value)}
              placeholder={t('weather.city_placeholder', 'e.g. Dakar')}
              aria-label={t('workspace:weatherCity')}
              autoFocus
              style={{
                flex: 1, minWidth: 0, padding: '8px 10px', borderRadius: 8,
                border: '1px solid #cbd5e1', fontSize: '0.9rem', outline: 'none',
              }}
            />
            <button
              type="submit"
              aria-label={t('workspace:weatherShow')}
              disabled={loading}
              style={{
                padding: '8px 14px', borderRadius: 8, border: 'none',
                background: '#059669', color: 'white', fontWeight: 700, cursor: 'pointer',
              }}
            >
              {loading ? '…' : '→'}
            </button>
          </form>
        )}
        {manualMode && permissionState === 'denied' && (
          <button type="button" className="ws-text-button" onClick={requestGeolocation}>
            📍 {t('workspace:weatherUseLocation')}
          </button>
        )}
        {error && <p style={{ color: '#b91c1c', marginTop: 8, fontSize: '0.85rem', textAlign: 'center' }}>{error}</p>}
      </>
    );
  }

  // ─────────── Loading ───────────
  if (loading && !data) {
    return shell(
      <>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 32 }}>🌦️</span>
          <div>
            <div style={{ fontWeight: 800, fontSize: '1rem' }}>{t('weather.title', "Today's weather")}</div>
            <div style={{ color: '#64748b', fontSize: '0.8rem' }}>{t('weather.loading', 'Loading…')}</div>
          </div>
        </div>
        <div className="weather-skeleton" style={{
          marginTop: 14, height: 90, borderRadius: 12,
          background: 'linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)',
          backgroundSize: '200% 100%',
        }} />
      </>
    );
  }

  // ─────────── Error ───────────
  if (error) {
    return shell(
      <>
        <div style={{ fontWeight: 800, marginBottom: 8 }}>🌦️ {t('weather.title', "Today's weather")}</div>
        <p style={{ color: '#b91c1c', margin: '0 0 10px' }}>{error}</p>
        <button
          type="button"
          onClick={resetLocation}
          style={{ padding: '6px 12px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}
        >
          {t('weather.change_location', 'Change location')}
        </button>
      </>
    );
  }

  // ─────────── Loaded (the main event) ───────────
  return shell(
    <>
      {/* Header */}
      <div className="weather-location" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.78rem', color: '#64748b', fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase' }}>
            <span>📍</span>
            <span>{displayLocation}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={resetLocation}
          style={{
            background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(148, 163, 184, 0.3)',
            color: '#475569', cursor: 'pointer', fontSize: '0.72rem', fontWeight: 600,
            padding: '4px 10px', borderRadius: 999,
          }}
          title={t('weather.change_location', 'Change location')}
        >
          {t('weather.change', 'Change')}
        </button>
      </div>

      {/* Hero — icon + big temperature + condition */}
      <div className="weather-hero" style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 14 }}>
        <div style={{ fontSize: 62, lineHeight: 1, filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.12))' }}>
          {view.icon}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
            <span style={{ fontSize: '2.6rem', fontWeight: 800, lineHeight: 1, color: view.accent }}>
              {w.temperature_c != null ? Math.round(w.temperature_c) : '—'}°
            </span>
            <span style={{ fontSize: '1rem', fontWeight: 600, color: '#64748b' }}>C</span>
          </div>
          <div style={{ fontSize: '0.85rem', color: '#334155', fontWeight: 600 }}>
            {conditionLabel(w.weather_code, w.is_day !== false)}
          </div>
          {(w.temperature_max_c != null || w.temperature_min_c != null) && (
            <div style={{ fontSize: '0.75rem', color: '#64748b', marginTop: 2 }}>
              ↑ {w.temperature_max_c != null ? Math.round(w.temperature_max_c) : '—'}°
              &nbsp;·&nbsp;
              ↓ {w.temperature_min_c != null ? Math.round(w.temperature_min_c) : '—'}°
            </div>
          )}
        </div>
      </div>

      {/* Stat pills */}
      <div className="weather-stats" style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 6, marginBottom: 14,
      }}>
        <StatPill icon="💧" label={t('weather.rain', 'Rain')} value={w.rain_probability_pct != null ? `${Math.round(w.rain_probability_pct)}%` : '—'} />
        <StatPill icon="💦" label={t('weather.humidity', 'Humidity')} value={w.humidity_pct != null ? `${Math.round(w.humidity_pct)}%` : '—'} />
        <StatPill
          icon="🌬️"
          label={t('weather.wind', 'Wind')}
          value={w.wind_speed_kmh != null ? `${Math.round(w.wind_speed_kmh)} km/h` : '—'}
          sub={compass(w.wind_direction_deg)}
        />
      </div>

      {/* Sun + UV mini row */}
      {(w.sunrise || w.sunset || w.uv_index_max != null) && (
        <div className="weather-sun" style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 8, padding: '8px 10px', marginBottom: 14,
          background: 'rgba(255, 255, 255, 0.55)', borderRadius: 10,
          border: '1px solid rgba(148, 163, 184, 0.18)',
          fontSize: '0.78rem', color: '#475569',
        }}>
          {w.sunrise && <span>🌅 {fmtTime(w.sunrise, lang)}</span>}
          {w.sunset && <span>🌇 {fmtTime(w.sunset, lang)}</span>}
          {w.uv_index_max != null && (
            <span>
              ☀️ UV <b style={{ color: w.uv_index_max >= 8 ? '#b91c1c' : w.uv_index_max >= 6 ? '#c2410c' : '#0f766e' }}>
                {Math.round(w.uv_index_max)}
              </b>
            </span>
          )}
        </div>
      )}

      {/* 5-day forecast */}
      {Array.isArray(w.daily_forecast) && w.daily_forecast.length > 0 && (
        <>
          <SectionLabel icon="📅" label={t('weather.forecast', '5-day forecast')} />
          <div style={{
            display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 14,
            background: 'rgba(255, 255, 255, 0.55)', borderRadius: 12,
            border: '1px solid rgba(148, 163, 184, 0.2)', padding: '6px 8px',
          }}>
            {w.daily_forecast.slice(0, 5).map((d, i) => {
              const dayView = wmoToView(d.weather_code, true);
              return (
                <div
                  key={i}
                  className="weather-forecast-day"
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '2.6em 1.6em 1fr auto',
                    alignItems: 'center', gap: 8,
                    padding: '4px 2px', fontSize: '0.8rem',
                    borderBottom: i === Math.min(4, w.daily_forecast.length - 1) ? 'none' : '1px dashed rgba(148, 163, 184, 0.25)',
                  }}
                >
                  <span style={{ fontWeight: 600, color: '#334155', textTransform: 'capitalize' }}>
                    {fmtDayShort(d.date, lang)}
                  </span>
                  <span style={{ fontSize: '1.1rem', textAlign: 'center' }}>{dayView.icon}</span>
                  <span style={{ color: '#0284c7', fontWeight: 600, fontSize: '0.72rem' }}>
                    💧 {d.rain_probability_pct != null ? `${Math.round(d.rain_probability_pct)}%` : '—'}
                  </span>
                  <span style={{ fontWeight: 700, color: '#0f172a', letterSpacing: 0.2, whiteSpace: 'nowrap' }}>
                    {d.temperature_max_c != null ? Math.round(d.temperature_max_c) : '—'}°
                    <span style={{ color: '#94a3b8', fontWeight: 500, margin: '0 4px' }}>/</span>
                    <span style={{ color: '#64748b', fontWeight: 500 }}>
                      {d.temperature_min_c != null ? Math.round(d.temperature_min_c) : '—'}°
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Farm advice — plain, richer labels from the LLM */}
      {advice.length > 0 && (
        <>
          <SectionLabel icon="🌱" label={t('weather.farm_advice', 'Farm advice')} />
          <ul style={{
            listStyle: 'none', padding: 0, margin: `0 0 ${alerts.length ? 14 : 4}px`,
            display: 'flex', flexDirection: 'column', gap: 8,
          }}>
            {advice.map((a, i) => (
              <li
                key={i}
                style={{
                  display: 'flex', alignItems: 'flex-start', gap: 8,
                  color: '#0f172a', fontSize: '0.85rem', lineHeight: 1.45,
                }}
              >
                <span style={{ fontSize: '1rem', marginTop: 1 }}>{a.icon}</span>
                <span style={{ flex: 1 }}>{a.label}</span>
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <>
          <SectionLabel icon="🚨" label={t('weather.alerts', 'Alerts')} color="#991b1b" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {alerts.map((al, i) => {
              const s = ALERT_STYLES[al.severity] || ALERT_STYLES.warn;
              return (
                <div
                  key={i}
                  className="weather-alert-pulse"
                  style={{
                    display: 'flex', alignItems: 'flex-start', gap: 8,
                    background: s.bg, borderLeft: `4px solid ${s.border}`, color: s.color,
                    padding: '8px 10px', borderRadius: 8, fontSize: '0.82rem',
                    lineHeight: 1.4,
                  }}
                >
                  <span style={{ fontSize: '1rem', marginTop: 1 }}>{s.icon}</span>
                  <span style={{ flex: 1 }}>{al.message}</span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </>,
    gradient,
  );
};

// ── Tiny presentational helpers ────────────────────────────────────────

const StatPill = ({ icon, label, value, sub }) => (
  <div style={{
    background: 'rgba(255, 255, 255, 0.6)',
    border: '1px solid rgba(148, 163, 184, 0.2)',
    borderRadius: 12, padding: '8px 6px',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
    textAlign: 'center',
  }}>
    <div style={{ fontSize: '1rem' }}>{icon}</div>
    <div style={{ fontSize: '0.9rem', fontWeight: 700, color: '#0f172a' }}>{value}</div>
    <div style={{ fontSize: '0.65rem', color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
      {label}{sub ? ` · ${sub}` : ''}
    </div>
  </div>
);

const SectionLabel = ({ icon, label, color }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
    color: color || '#0f172a', fontWeight: 700, fontSize: '0.82rem',
    textTransform: 'uppercase', letterSpacing: 0.5,
  }}>
    <span>{icon}</span>
    <span>{label}</span>
  </div>
);

export default WeatherCard;
